import ConnectyCube from "connectycube";
import { CallEvent, Calls, CallSessionConnectionState } from "connectycube/types";
import { useShallow } from "zustand/react/shallow";
import { CallsLogger } from "../helpers";
import useCallsStore, { CallDataType } from "./useCallsStore";
import useCallsStoreRef from "./useCallsStoreRef";

export interface CallsListeners {
  setupListeners: () => void;
}

const useCallsListeners = (): CallsListeners => {
  const [
    setIncomingCall,
    resetIncomingCall,
    addRemoteStream,
    removeRemoteStream,
    setDeviceChangeEvent,
    upsertSessionData,
    participantDidEnter,
    participantDidLeave,
    activateCallsListeners,
  ] = useCallsStore(
    useShallow((store) => [
      store.setIncomingCall,
      store.resetIncomingCall,
      store.addRemoteStream,
      store.removeRemoteStream,
      store.setDeviceChangeEvent,
      store.upsertSessionData,
      store.participantDidEnter,
      store.participantDidLeave,
      store.activateCallsListeners,
    ]),
  );
  const activeCallRef = useCallsStoreRef("activeCall");
  const incomingCallRef = useCallsStoreRef("incomingCall");

  const handleOnCall = (session: Calls.Session, userInfo: Calls.UserInfo) => {
    if (session.initiatorID === session.currentUserID) {
      return;
    }

    setIncomingCall(session);
    upsertSessionData(CallDataType.ON_CALL, { userInfo });
    CallsLogger.log("onCall", { session, ext: userInfo });
  };

  const handleOnAccept = (session: Calls.Session, userID: number, userInfo: Calls.UserInfo) => {
    participantDidEnter(userID);
    upsertSessionData(CallDataType.ON_ACCEPT, { userID, userInfo });
    CallsLogger.log("onAccept", { session, userID, ext: userInfo });
  };

  const handleOnReject = (session: Calls.Session, userID: number, userInfo: Calls.UserInfo) => {
    participantDidLeave(userID);
    upsertSessionData(CallDataType.ON_REJECT, { userID, userInfo });
    CallsLogger.log("onReject", { session, userID, ext: userInfo });
  };

  const handleOnStop = (session: Calls.Session, userID: number, userInfo: Calls.UserInfo) => {
    if (activeCallRef.current?.ID === session.ID) {
      participantDidLeave(userID);
      removeRemoteStream(userID);
      upsertSessionData(CallDataType.ON_STOP, { userID, userInfo });
    }

    if (incomingCallRef.current?.ID === session.ID) {
      upsertSessionData(CallDataType.ON_STOP_CALLING, { userID, userInfo });
      resetIncomingCall();
    }

    CallsLogger.log("onStop", { session, userID, ext: userInfo });
  };

  const handleOnNotAnswer = (session: Calls.Session, userID: number) => {
    participantDidLeave(userID);
    upsertSessionData(CallDataType.ON_NOT_ANSWER, { userID });
    CallsLogger.log("onNotAnswer", { session, userID });
  };

  const handleOnRemoteStream = (session: Calls.Session, userID: number, stream: MediaStream) => {
    addRemoteStream(userID, stream);
    CallsLogger.log("onRemoteStream", { session, userID, stream });
  };

  const handleOnConnectionState = (
    session: Calls.Session,
    userID: number,
    connectionState: CallSessionConnectionState,
  ) => {
    upsertSessionData(CallDataType.ON_CONNECTION_STATE, { userID, connectionState });
    CallsLogger.log("onConnectionState", { session, userID, connectionState });
  };

  const handleOnDevicesChange = (event: Event) => {
    setDeviceChangeEvent(event);
    CallsLogger.log("onDevicesChange", { event });
  };

  const setupListeners = () => {
    ConnectyCube.videochat.addListener(CallEvent.CALL, handleOnCall);
    ConnectyCube.videochat.addListener(CallEvent.ACCEPT, handleOnAccept);
    ConnectyCube.videochat.addListener(CallEvent.REJECT, handleOnReject);
    ConnectyCube.videochat.addListener(CallEvent.STOP, handleOnStop);
    ConnectyCube.videochat.addListener(CallEvent.NOT_ANSWER, handleOnNotAnswer);
    ConnectyCube.videochat.addListener(CallEvent.REMOTE_STREAM, handleOnRemoteStream);
    ConnectyCube.videochat.addListener(CallEvent.CONNECTION_STATE, handleOnConnectionState);
    ConnectyCube.videochat.addListener(CallEvent.DEVICES, handleOnDevicesChange);
    activateCallsListeners();
  };

  return {
    setupListeners,
  };
};

export default useCallsListeners;
